import {
  APPOINTMENT_TYPES,
  BaseRouteState,
  POLICY_TYPES,
  Policy,
  SocialReferent,
} from '@shared/data-access'
import { AvailableSR, GenerateRoomModel } from '@sr/data-access'
import { actions } from '@sr/routes/common/store'
import { flow, getRoot, types } from 'mobx-state-tree'

export const CareAppointmentState = BaseRouteState.named('CareAppointmentState')
  .props({
    isEdit: false,
    eventId: types.string,
    socialReferentId: types.maybeNull(types.string),
    appointmentType: types.number,
    policies: types.array(types.reference(Policy)),
    policy: types.maybeNull(types.reference(Policy)),
    policyId: types.maybeNull(types.string),
    user: types.maybeNull(types.reference(SocialReferent)),
    canGenerateRoom: types.maybeNull(types.reference(GenerateRoomModel)),
    search: types.maybeNull(types.string),
    page: types.maybeNull(types.integer, 0),
    isTravelTime: types.optional(types.boolean, false),
    availableSocialReferentList: types.maybeNull(
      types.array(types.safeReference(AvailableSR)),
    ),
  })
  .views((self) => ({
    // Always return clean eventId without travel suffixes for backend operations
    get cleanEventId() {
      return self.eventId ? self.eventId.replace(/-travel-(before|after)$/, '') : self.eventId
    },
    get isOwner() {
      return self.socialReferentId === self.user?.id
    },
    get isPreControl() {
      return (
        self.appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT ||
        self.appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT_FACE_TO_FACE
      )
    },
    get policiesWithDefault() {
      return self.policy &&
        !self.policies.some(
          (policy) => policy.id === self.policy.id,
        )
        ? [...self.policies, self.policy]
        : self.policies
    },
    get policyClients() {
      return self.policiesWithDefault?.map(({ id, fullName }) => ({
        value: id,
        name: fullName,
      }))
    },
    get isCoordinatorInHisCalendar() {
      return self.user?.isCoordinator && self.user?.id === self.socialReferentId
    },
    get paginatedId() {
      return self.isCoordinatorInHisCalendar ? null : self.socialReferentId
    },
    get isSubscriptionControl() {
      return (
        self.appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT ||
        self.appointmentType === APPOINTMENT_TYPES.INITIAL_REPORT_FACE_TO_FACE
      )
    },
    get currentSocialReferent() {
      const policySocialReferent = self.policy?.socialReferent
      const availableListReferent = self.availableSocialReferentList?.find(
        (socialReferent) => socialReferent?.id === self.socialReferentId,
      )
      const fallbackUser = self.user
      const resolved =
        policySocialReferent || availableListReferent || fallbackUser


      return resolved
    },
    get routeSocialReferent() {
      if (!self.socialReferentId) {
        return null
      }

      const rootStore = getRoot(self)
      const store = rootStore?.SocialReferentStore

      return store?.map?.get?.(self.socialReferentId) || null
    },
    get availableSocialReferentOptions() {
      const options =
        self.availableSocialReferentList?.map(({ id, fullName }) => ({
          value: id,
          name: fullName,
        })) || []

      const ensureOption = (socialReferent) => {
        if (
          socialReferent &&
          !options.some((option) => option.value === socialReferent.id)
        ) {
          options.unshift({
            value: socialReferent.id,
            name:
              socialReferent.fullName ||
              [socialReferent.name, socialReferent.surname]
                .filter(Boolean)
                .join(' ')
                .trim(),
          })
        }
      }

      ensureOption(self.currentSocialReferent)

      return options
    },
  }))
  .actions((self) => ({
    afterAttach() {
      self.loadDependencies()
    },
    loadDependencies: flow(function* loadDependencies() {
      self.setLoading('CareAppointmentState')
      try {
        // For travel times, skip most dependencies loading to avoid 404 errors
        if (self.isTravelTime) {
          yield self.loadUserInfo()
          // Skip policy loading and other dependencies for travel times
          return
        }

        yield self.loadUserInfo()

        self.policies = yield self.loadPaginatedPolicies({
          isClear: true,
          socialReferentId: self.paginatedId,
          policyType: POLICY_TYPES.policy,
        })
        if (self.policyId) {
          yield self.loadPolicy()
          if (self.isSubscriptionControl && self.user?.isCoordinator) {
            yield self.loadAvailableSrForChangeCare()
          }
        }
        if (self.isEdit) {
          yield self.checkIfCanGenerateRoom(self.cleanEventId)
        }
      } finally {
        self.removeLoading('CareAppointmentState')
      }
    }),
    setAppointmentType(appointmentType) {
      self.appointmentType = appointmentType
    },
    setSearch: flow(function* setSearch(search) {
      self.search = search
      self.page = 0
      self.policies = yield self.loadPaginatedPolicies({
        isClear: true,
        socialReferentId: self.paginatedId,
        policyType: POLICY_TYPES.policy,
      })
    }),
    ...actions(self),
  }))
